import React from 'react';
import PageJump from './PageJump';
import { getPageNumbers } from '../../utils/pageJump';

/**
 * 需求 R117 R3：四頁共用的分頁列。
 *
 * PM 2026-08-18 留言：
 *   「本任務已確認完成，但也發現每個頁面的頁數呈現排列都不同，請對齊。
 *     排列方式對齊『客戶管理』頁面」
 *
 * 在此之前四頁的分頁列是各寫各的，排列完全不同：
 *   產品管理  ：筆數資訊 → 每頁顯示 → 頁碼（上一頁／…／下一頁）
 *   報價單    ：筆數資訊 → 每頁顯示 → 頁碼（上一頁／…／下一頁）
 *   型錄管理  ：每頁顯示 → 頁碼（上一頁／…／下一頁），整列置中、且沒有筆數資訊
 *   客戶管理  ：每頁顯示 → 頁碼（‹／…／›）→ 筆數資訊      ← PM 指定的基準
 *
 * 本元件把「客戶管理」那一套抽出來，四頁一律改成 import 這個元件，
 * 排列／換頁鈕符號／省略號寫法／筆數文案從此只有一份實作，不會再各自長歪。
 *
 * ── 對齊後的固定排列（＝客戶管理原本的 DOM 順序）──
 *   .pagination-container（justify-content: space-between）
 *     ├─ .page-size-selector  「每頁顯示 [n] 筆」          （最左）
 *     ├─ .pagination          ‹ ／ 頁碼 ／ › ／ 跳頁框      （置中）
 *     └─ .pagination-info     「顯示第 X–Y 筆，共 N 筆」    （最右）
 *
 * 類名沿用既有的全域 .pagination-container / .pagination / .page-btn /
 * .page-size-selector / .pagination-info（CRA 的 CSS 全部全域化，需求 R72），
 * 所以四頁的外觀本來就同一套規則，這次不需要、也刻意不新增任何 CSS。
 * 型錄管理原本自己一套 .catalog-pagination / .catalog-page-btn 就是它跟另三頁長不一樣的原因，
 * 改用本元件後那組規則已無人引用，一併從 CatalogPage.css 移除。
 *
 * 需求 R117 原本拍板的 Q1–Q8 行為（跳頁框 PageJump）完全不變，只是改由本元件統一插在最後。
 *
 * @param {number}   page             目前頁碼（1 起算）
 * @param {number}   totalPages       總頁數（0／NaN 會被當成 1 頁處理）
 * @param {number}   total            資料總筆數
 * @param {number}   pageSize         每頁筆數
 * @param {number[]} pageSizeOptions  「每頁顯示」下拉選項（各頁沿用自己原本的選項，
 *                                     產品／報價單 25/50/100、客戶／型錄 20/50/100）
 * @param {Function} onPageChange     換頁 callback，收到 1..totalPages 的整數
 * @param {Function} onPageSizeChange 改每頁筆數 callback，收到數字；
 *                                    呼叫端自行負責把頁碼重設回第 1 頁（維持各頁原本寫法）
 */
const Pagination = ({
  page,
  totalPages,
  total,
  pageSize,
  pageSizeOptions = [20, 50, 100],
  onPageChange,
  onPageSizeChange,
}) => {
  // total=0 時 Math.ceil(0/n) 會是 0；比照客戶管理的 `Math.ceil(total / limit) || 1`，
  // 讓「無資料」也還是顯示一頁的分頁列（型錄管理原本是整條不渲染，這次一併對齊）。
  const pages = Number(totalPages) > 0 ? Number(totalPages) : 1;
  const startIdx = total > 0 ? (page - 1) * pageSize + 1 : 0;
  const endIdx = Math.min(page * pageSize, total);

  return (
    <div className="pagination-container">
      <div className="page-size-selector">
        <span>每頁顯示</span>
        <select value={pageSize} onChange={(e) => onPageSizeChange(Number(e.target.value))}>
          {pageSizeOptions.map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
        <span>筆</span>
      </div>

      <div className="pagination">
        <button className="page-btn" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>‹</button>
        {/*
          頁碼視窗一律走 utils/pageJump.getPageNumbers（≤7 頁全列，否則 1 … 目前頁±1 … 末頁）。
          產品管理／報價單原本是自己那套 maxVisible=5 的視窗、型錄管理是同演算法但 span 省略號，
          對齊後統一成客戶管理的寫法：省略號＝disabled 的 .page-btn。
        */}
        {getPageNumbers(page, pages).map((p, i) => (
          p === '...'
            ? <button key={`dots-${i}`} className="page-btn" disabled>...</button>
            : <button
                key={p}
                className={`page-btn${p === page ? ' active' : ''}`}
                onClick={() => onPageChange(p)}
              >
                {p}
              </button>
        ))}
        <button className="page-btn" disabled={page >= pages} onClick={() => onPageChange(page + 1)}>›</button>
        {/* 需求 R117 Q2=A：跳頁框接在最後一顆換頁鈕右側；Q6=A 只有一頁時 PageJump 自己回 null。 */}
        <PageJump page={page} totalPages={pages} onJump={onPageChange} />
      </div>

      <div className="pagination-info">
        {total > 0 ? `顯示第 ${startIdx}–${endIdx} 筆，共 ${Number(total).toLocaleString()} 筆` : '無資料'}
      </div>
    </div>
  );
};

export default Pagination;
